import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CONSENT_VERSIONS, isGranted, type ConsentPurpose } from '@il-colpo/shared/account';
import { getAccountStore, useAccount } from '../../account/accountStore.ts';
import { useToastStore } from '../../state/toastStore.ts';
import { Button, Modal, Panel, ScreenFrame, Switch } from '../../ui/index.ts';
import styles from './Account.module.css';
import { FormError, Page, useAccountSession, useAction, useDateFormat } from './common.tsx';

/** Hands the browser a file to save. */
function download(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}

function ConsentRow({ purpose }: { purpose: 'terms' | 'privacy' }) {
  const { t } = useTranslation('account');
  const consents = useAccount((s) => s.consents);
  const entry = consents?.[purpose];
  const granted = consents ? isGranted(consents, purpose) : false;
  const outdated = entry?.granted === true && entry.version !== CONSENT_VERSIONS[purpose];
  return (
    <div className={styles['stack']}>
      <div className={styles['row']}>
        <strong>{t(purpose === 'terms' ? 'privacy.termsLabel' : 'privacy.privacyLabel')}</strong>
        <Link className={styles['link']} to={`/legal/${purpose}`}>
          {t('consent.read')}
        </Link>
      </div>
      <span className={styles['note']}>
        {granted
          ? t('privacy.granted', { version: entry?.version ?? '' })
          : outdated
            ? t('privacy.outdated')
            : t('privacy.notGranted')}
      </span>
    </div>
  );
}

function Consents() {
  const { t } = useTranslation('account');
  const consents = useAccount((s) => s.consents);
  const { error, run } = useAction();
  const purpose: ConsentPurpose = 'analytics';
  return (
    <Panel title={t('privacy.consentsTitle')}>
      <div className={styles['stack']}>
        <ConsentRow purpose="terms" />
        <ConsentRow purpose="privacy" />
        <p className={styles['note']}>{t('privacy.withdrawNote')}</p>
        <Switch
          checked={consents ? isGranted(consents, purpose) : false}
          onChange={(checked) => {
            void run(() => getAccountStore().getState().answerConsent(purpose, checked));
          }}
          label={t('privacy.analyticsLabel')}
          hint={t('privacy.analyticsBody')}
          disabled={consents === null}
        />
        <FormError code={error} />
      </div>
    </Panel>
  );
}

function Export() {
  const { t } = useTranslation('account');
  const client = useAccount((s) => s.client);
  const { pending, error, run } = useAction();
  const [done, setDone] = useState(false);
  return (
    <Panel title={t('privacy.exportTitle')}>
      <div className={styles['stack']}>
        <p className={styles['note']}>{t('privacy.exportBody')}</p>
        <FormError code={error} />
        {done ? <p className={styles['okNote']}>{t('privacy.exportDone')}</p> : null}
        <div>
          <Button
            variant="secondary"
            loading={pending}
            onClick={() => {
              if (!client) return;
              setDone(false);
              void run(() => client.exportData()).then((r) => {
                if (!r.ok) return;
                download(
                  `il-colpo-data-${new Date().toISOString().slice(0, 10)}.json`,
                  JSON.stringify(r.value, null, 2),
                );
                setDone(true);
              });
            }}
          >
            {t('privacy.exportAction')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function Deletion() {
  const { t } = useTranslation('account');
  const navigate = useNavigate();
  const deletion = useAccount((s) => s.deletion);
  const { date } = useDateFormat();
  const [open, setOpen] = useState(false);
  const { pending, error, run } = useAction();

  const remove = async (graceDays: number) => {
    const r = await run(() => getAccountStore().getState().requestDeletion(graceDays));
    if (!r.ok) return;
    setOpen(false);
    if (graceDays === 0) {
      useToastStore.getState().push(t('privacy.deleteDone'), 'success');
      void navigate('/account', { replace: true });
    }
  };

  return (
    <Panel title={t('privacy.deleteTitle')}>
      <div className={styles['stack']}>
        <p className={styles['note']}>{t('privacy.deleteBody')}</p>
        {deletion ? (
          <>
            <strong role="status">
              {t('privacy.deleteScheduled', { date: date(deletion.scheduledFor) })}
            </strong>
            <div>
              <Button
                loading={pending}
                onClick={() => {
                  void run(() => getAccountStore().getState().cancelDeletion()).then((r) => {
                    if (r.ok) useToastStore.getState().push(t('deletion.cancelled'), 'info');
                  });
                }}
              >
                {t('deletion.cancel')}
              </Button>
            </div>
          </>
        ) : (
          <div>
            <Button
              variant="danger"
              onClick={() => {
                setOpen(true);
              }}
            >
              {t('privacy.deleteAction')}
            </Button>
          </div>
        )}
        <FormError code={error} />
      </div>
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        title={t('privacy.deleteDialogTitle')}
        actions={
          <>
            <Button
              loading={pending}
              onClick={() => {
                void remove(7);
              }}
            >
              {t('privacy.deleteLater')}
            </Button>
            <Button
              variant="danger"
              loading={pending}
              onClick={() => {
                void remove(0);
              }}
            >
              {t('privacy.deleteNow')}
            </Button>
          </>
        }
      >
        <p className={styles['note']}>{t('privacy.deleteDialogBody')}</p>
      </Modal>
    </Panel>
  );
}

export function PrivacyScreen() {
  const { t } = useTranslation('account');
  const { available, load, snapshot } = useAccountSession();

  if (!available || (load === 'ready' && snapshot.status === 'signedOut')) {
    return <Navigate to="/account" replace />;
  }
  return (
    <ScreenFrame title={t('privacy.title')} backTo="/account">
      <Page>
        {snapshot.status === 'signedIn' ? (
          <>
            <Consents />
            <Export />
            <Deletion />
          </>
        ) : (
          <Panel>
            <p className={styles['note']} role="status">
              {t('loading')}
            </p>
          </Panel>
        )}
      </Page>
    </ScreenFrame>
  );
}
